import { Injectable, inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { envs } from '@environments';
import {
  Episode,
  EpisodeReleaseStatus,
  Program,
  ProgramPlayType,
  ProgramWithSeasons,
} from '@shared/graphql/api.generated';
import { ogSiteName } from '@shared/helper/configs';
import { LinkDefinition } from '@shared/seo/link.service';
import { SeoService } from '@shared/seo/seo.service';
import { SchemaService } from './schema.service';

@Injectable()
export class ProgramSeoService {
  private readonly seoService = inject(SeoService);
  private readonly schemaService = inject(SchemaService);

  setSeoTags(program: Program) {
    this.seoService.removeMetaTags();
    this.setTitle(program);
    this.setMetaTags(program);
    this.setLinkTags(program);
  }

  private programTitle(program: Program): string {
    return (
      (program.playType === ProgramPlayType.OnlineScreening
        ? 'انیمیشن '
        : program.isSolitary
          ? 'فیلم '
          : 'سریال ') + program.title
    );
  }

  private handleProgramTitle(program: Program): string {
    const constanceTitle = ' - تماشای آنلاین در شیدا';
    const constanceTitleOS = ' - اکران آنلاین فیلم های سینمایی با بالاترین کیفیت در شیدا';
    return `${this.programTitle(program)}${
      program.playType === ProgramPlayType.InSite ? constanceTitle : constanceTitleOS
    }`;
  }

  private handleProgramKeyword(program: Program): string {
    const keywords = [
      'فیلم',
      'سریال',
      `پخش آنلاین ${this.programTitle(program)}`,
      `دانلود ${this.programTitle(program)}`,
      `مشاهده ${this.programTitle(program)}`,
      `مشاهده آنلاین ${this.programTitle(program)}`,
    ];
    if (program.playType === ProgramPlayType.OnlineScreening) {
      keywords.push('اکران آنلاین');
    }
    return keywords.join(', ');
  }

  private setTitle(program: Program) {
    this.seoService.setTitle(this.handleProgramTitle(program));
  }

  private setMetaTags(program: Program) {
    const tags: MetaDefinition[] = [
      {
        name: 'title',
        content: this.handleProgramTitle(program),
      },
      {
        name: 'description',
        content: program.description,
      },
      {
        name: 'keywords',
        content: this.handleProgramKeyword(program),
      },
      {
        name: 'Uid',
        content: program.uid,
      },
      {
        property: 'og:url',
        content: `${envs.siteUrl}/p/${program.uid}`,
      },
      {
        property: 'og:title',
        content: this.handleProgramTitle(program),
      },
      {
        property: 'og:description',
        content: program.description,
      },
      {
        property: 'og:image',
        content: program.landscapeImagePath,
      },
      {
        property: 'og:site_name',
        content: ogSiteName,
      },
      {
        property: 'og:type',
        content: 'video.movie',
      },

      // {
      //   property: 'og:video',
      //   content: 'trailer url',
      // },
      // {
      //   property: 'og:video:secure_url',
      //   content: 'trailer url',
      // },
      {
        property: 'og:image:width',
        content: '1280',
      },
      {
        property: 'og:image:height',
        content: '720',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: this.handleProgramTitle(program),
      },
      {
        name: 'twitter:description',
        content: program.description,
      },
      {
        name: 'twitter:image',
        content: program.landscapeImagePath,
      },
    ];
    this.seoService.setMetaTags(tags);
  }

  private setLinkTags(program: Program) {
    const tags: LinkDefinition[] = [
      {
        rel: 'image_src',
        href: program.landscapeImagePath,
      },
      {
        rel: 'thumbnailUrl',
        href: program.landscapeImagePath,
      },
      {
        rel: 'canonical',
        href: `${envs.siteUrl}/p/${program.uid}`,
      },
    ];
    this.seoService.setLinkTag(tags);
  }

  private setSolitaryProgramJsonLd(program: ProgramWithSeasons) {
    let defaultTrailer = program.trailers.find((trailer) => trailer?.isDefault);
    if (!defaultTrailer) defaultTrailer = program.trailers[0];

    const jsonData: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      name: program.program.title,
      description: program.program.description,
      image: {
        '@type': 'ImageObject',
        url: program.program.bannerImagePath,
      },
      actor: program.cast
        .find((cast) => cast?.role === 'PERFORMER')
        ?.artists?.map((cast) => ({
          '@type': 'Person',
          name: cast?.fullName,
        })),
      director: {
        '@type': 'Person',
        name: program.cast
          .find((cast) => cast?.role === 'DIRECTOR')
          ?.artists?.map((director) => director.fullName)
          .join(', '),
      },
      dateCreated: program.solitaryEpisode?.createdAt ?? program.program.createdAt,
      dateModified: program.lastEpisode?.updatedAt,
      trailer: {
        '@type': 'VideoObject',
        name: defaultTrailer?.title,
        description: program.program.description,
        thumbnailUrl: defaultTrailer?.coverImagePath,
        duration: this.schemaService.handleDuration(defaultTrailer?.duration!),
        contentUrl: `${envs.siteUrl}/pr/${defaultTrailer?.id}`,
        embedUrl: `${envs.siteUrl}/pr/${defaultTrailer?.id}`,
        uploadDate: defaultTrailer?.createdAt,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        bestRating: '100',
        description:
          'The Sheyda rating is a measurement of movie and Series quality for moviegoers. It represents the percentage of viewers reviews that are positive for a given film or series.',
        ratingCount: program.program.statisticsDetails.totalInterestCount.toString(),
        ratingValue: (
          program.program.statisticsDetails.totalLikePercentage +
          program.program.statisticsDetails.totalLovePercentage
        ).toString(),
        worstRating: '0',
      },
      // TODO: Uncomment with real data
      // "interactionStatistic":
      // {
      //   "@type": "InteractionCounter",
      //   "interactionType": "https://schema.org/CommentAction",
      //   "userInteractionCount": "11"
      // }
    };

    if (program.solitaryEpisode?.releaseDate) {
      jsonData['datePublished'] = program.solitaryEpisode?.releaseDate;
    } else if (program.solitaryEpisode?.releaseStatus === EpisodeReleaseStatus.Released) {
      jsonData['datePublished'] = program.solitaryEpisode.createdAt;
    }

    this.schemaService.setJsonLd(jsonData);
  }

  private setSeriesJsonLd(program: ProgramWithSeasons, episodes: Episode[]) {
    let defaultTrailer = program.trailers.find((trailer) => trailer?.isDefault);
    if (!defaultTrailer) defaultTrailer = program.trailers[0];

    const jsonData: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'TVSeries',
      name: program.program.title,
      description: program.program.description,
      image: {
        '@type': 'ImageObject',
        url: program.program.bannerImagePath,
      },
      numberOfSeasons: program.seasons.length,
      dateCreated: program.program.createdAt,
      dateModified: program.lastEpisode?.updatedAt,
      actor: program.cast
        .find((cast) => cast?.role === 'PERFORMER')
        ?.artists?.map((cast) => ({
          '@type': 'Person',
          name: cast?.fullName,
        })),
      director: {
        '@type': 'Person',
        name: program.cast
          .find((cast) => cast?.role === 'DIRECTOR')
          ?.artists?.map((director) => director.fullName)
          .join(', '),
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        bestRating: '100',
        description:
          'The Sheyda rating is a measurement of movie and Series quality for moviegoers. It represents the percentage of viewers reviews that are positive for a given film or series.',
        ratingCount: program.program.statisticsDetails.totalInterestCount.toString(),
        ratingValue: (
          program.program.statisticsDetails.totalLikePercentage +
          program.program.statisticsDetails.totalLovePercentage
        ).toString(),
        worstRating: '0',
      },
      genre: program.program.genres?.map((genre) => genre.name),
      // TODO: Uncomment with real data
      // "subtitleLanguage": ["en", "fa"],
      // "inLanguage": ["fa", "en"],
      trailer: {
        '@type': 'VideoObject',
        name: defaultTrailer?.title,
        url: `${envs.siteUrl}/pr/${defaultTrailer?.id}`,
        duration: this.schemaService.handleDuration(defaultTrailer?.duration!),
        description: program.program.description,
        thumbnailUrl: defaultTrailer?.coverImagePath,
        contentUrl: `${envs.siteUrl}/pr/${defaultTrailer?.id}`,
        uploadDate: defaultTrailer?.createdAt,
        embedUrl: `${envs.siteUrl}/pr/${defaultTrailer?.id}`,
        publisher: {
          '@type': 'Organization',
          name: 'پلتفرم فیلم و سریال شیدا',
          '@id': `${envs.siteUrl}`,
        },
        video: {
          '@type': 'VideoObject',
          name: this.programTitle(program.program),
          description: program.program.description,
          thumbnailUrl: program.program.bannerImagePath,
          uploadDate: program.solitaryEpisode?.createdAt,
        },
        containsSeason: {
          '@type': 'TVSeason',
          name:
            program.seasons.length === 1
              ? `فصل اول ${this.programTitle(program.program)}`
              : `${program.seasons?.at(0)?.title} ${this.programTitle(program.program)}`,
          description: program.seasons?.at(0)?.description,
          image: program.seasons?.at(0)?.landscapeImagePath,
          episode: episodes.map((item, index) => ({
            '@type': 'TVEpisode',
            position: index + 1,
            episodeNumber: item.order,
            url: `${envs.siteUrl}/e/${item.uid}`,
          })),
        },
      },
    };

    if (program.solitaryEpisode?.releaseDate) {
      jsonData['datePublished'] = program.solitaryEpisode?.releaseDate;
      jsonData['startDate'] = program.solitaryEpisode?.releaseDate;
    } else if (program.solitaryEpisode?.releaseStatus === EpisodeReleaseStatus.Released) {
      jsonData['datePublished'] = program.solitaryEpisode.createdAt;
      jsonData['startDate'] = program.solitaryEpisode.createdAt;
    }

    this.schemaService.setJsonLd(jsonData);
  }
  //FROM HERE
  setProgramJsonLd(program: ProgramWithSeasons, episodes: Episode[]) {
    if (program.program.isSolitary) this.setSolitaryProgramJsonLd(program);
    else this.setSeriesJsonLd(program, episodes);
  }

  setRelatedProgramsJsonLd(relatedPrograms: Program[]) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: relatedPrograms.map((program, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${envs.siteUrl}/p/${program.uid}`,
      })),
    });
  }

  setEpisodeListJsonLd(programs: Episode[]) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: programs.map((program, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${envs.siteUrl}/e/${program.uid}`,
      })),
    });
  }

  setProgramBreadCrumbs(program: ProgramWithSeasons) {
    const breadcrumbs = [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'شیدا',
            item: `${envs.siteUrl}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: program.program.isSolitary ? 'فیلم سینمایی' : 'سریال',
            item: `${envs.siteUrl}/c/${program.program.isSolitary ? 'movie' : 'series'}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: this.programTitle(program.program),
          },
        ],
      },
    ];
    program.genres?.slice(0, 2).map((genre) => {
      breadcrumbs.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ژانرهای فیلم و سریال',
            item: `${envs.siteUrl}/g`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: genre?.name!,
            item: `${envs.siteUrl}/g/${genre!.descriptor}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: this.programTitle(program.program),
          },
        ],
      });
    });
    this.schemaService.setJsonLd(breadcrumbs);
  }
}
